import React, { useState, useEffect, useCallback } from 'react';
import { SlidersHorizontal, X, ChevronDown } from 'lucide-react';
import { api } from '../api';
import { useApp } from '../contexts/AppContext';
import SheepCard from '../components/SheepCard';
import SheepIcon from '../components/SheepIcon';

const SORT_OPTIONS = [
  { value: 'newest',     labelAr: 'الأحدث',       labelEn: 'Newest'   },
  { value: 'price_asc',  labelAr: 'السعر ↑',      labelEn: 'Price ↑'  },
  { value: 'price_desc', labelAr: 'السعر ↓',      labelEn: 'Price ↓'  },
];

const GENDER_OPTIONS = [
  { value: '',       labelAr: 'الكل',    labelEn: 'All'    },
  { value: 'male',   labelAr: '♂ ذكر',  labelEn: '♂ Male' },
  { value: 'female', labelAr: '♀ أنثى', labelEn: '♀ Female' },
];

export default function HomePage({ search }) {
  const { t, lang } = useApp();
  const [sheep,       setSheep]       = useState([]);
  const [breeds,      setBreeds]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [page,        setPage]        = useState(1);
  const [hasMore,     setHasMore]     = useState(false);
  const [total,       setTotal]       = useState(0);
  const [gender,      setGender]      = useState('');
  const [breed,       setBreed]       = useState('');
  const [sort,        setSort]        = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    api.getBreeds()
      .then(r => setBreeds(r.data.breeds_ar || []))
      .catch(() => {});
  }, []);

  const fetchSheep = useCallback(async (reset = true) => {
    setLoading(true);
    try {
      const p = reset ? 1 : page + 1;
      const res = await api.listSheep({ page: p, per_page: 20, search, gender, breed_ar: breed, sort });
      const data = res.data;
      setSheep(prev => reset ? data.sheep : [...prev, ...data.sheep]);
      setPage(p);
      setHasMore(p < data.pages);
      setTotal(data.total);
    } catch {}
    setLoading(false);
  }, [search, gender, breed, sort, page]);

  useEffect(() => { fetchSheep(true); }, [search, gender, breed, sort]);

  const activeFilters = [gender, breed].filter(Boolean).length;

  const clearAll = () => { setGender(''); setBreed(''); };

  return (
    <main className="page-pb">

      {/* ── Filter bar ── */}
      <div
        className="filter-bar"
        style={{
          background: 'var(--surface)',
          padding: '10px 16px 0',
          borderBottom: '1px solid var(--border)',
          position: 'sticky',
          top: 0,
          zIndex: 30,
        }}
      >
        {/* Scrollable chips row */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            alignItems: 'center',
            paddingBottom: 10,
          }}
        >
          {/* Filter toggle */}
          <button
            className={`chip${activeFilters > 0 ? ' active' : ''}`}
            onClick={() => setShowFilters(v => !v)}
            aria-expanded={showFilters}
            aria-label={lang === 'ar' ? 'فلتر' : 'Filter'}
          >
            <SlidersHorizontal size={13} />
            {lang === 'ar' ? 'فلتر' : 'Filter'}
            {activeFilters > 0 && (
              <span
                style={{
                  background: 'rgba(255,255,255,0.25)',
                  borderRadius: 'var(--r-full)',
                  width: 16,
                  height: 16,
                  fontSize: 10,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {activeFilters}
              </span>
            )}
            <ChevronDown
              size={12}
              style={{ transform: showFilters ? 'rotate(180deg)' : 'none', transition: 'transform var(--ease)' }}
            />
          </button>

          {/* Sort chips */}
          {SORT_OPTIONS.map(o => (
            <button
              key={o.value}
              className={`chip${sort === o.value ? ' active' : ''}`}
              onClick={() => setSort(o.value)}
            >
              {lang === 'ar' ? o.labelAr : o.labelEn}
            </button>
          ))}
        </div>

        {/* Expanded filter panel */}
        {showFilters && (
          <div
            style={{
              paddingBottom: 12,
              borderTop: '1px solid var(--border)',
              paddingTop: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {/* Gender row */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 600, flexShrink: 0, minWidth: 44 }}>
                {lang === 'ar' ? 'الجنس' : 'Gender'}
              </span>
              {GENDER_OPTIONS.map(g => (
                <button
                  key={g.value}
                  className={`chip${gender === g.value ? ' active' : ''}`}
                  onClick={() => setGender(g.value)}
                >
                  {lang === 'ar' ? g.labelAr : g.labelEn}
                </button>
              ))}
            </div>

            {/* Breed row */}
            {breeds.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 600, flexShrink: 0, minWidth: 44 }}>
                  {lang === 'ar' ? 'النوع' : 'Breed'}
                </span>
                <button
                  className={`chip${breed === '' ? ' active' : ''}`}
                  onClick={() => setBreed('')}
                >
                  {lang === 'ar' ? 'الكل' : 'All'}
                </button>
                {breeds.map(b => (
                  <button
                    key={b}
                    className={`chip${breed === b ? ' active' : ''}`}
                    onClick={() => setBreed(b)}
                  >
                    {b}
                  </button>
                ))}
              </div>
            )}

            {/* Clear filters */}
            {activeFilters > 0 && (
              <button
                onClick={clearAll}
                style={{
                  alignSelf: 'flex-start',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  background: 'none',
                  border: 'none',
                  color: 'var(--brand)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: 0,
                  fontFamily: 'inherit',
                }}
              >
                <X size={12} />
                {lang === 'ar' ? 'مسح الكل' : 'Clear all'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Results count ── */}
      {!loading && sheep.length > 0 && (
        <div
          className="count-row"
          style={{
            padding: '10px 16px 4px',
            fontSize: 12,
            color: 'var(--text-3)',
            fontWeight: 500,
          }}
        >
          {total} {lang === 'ar' ? 'خروف متاح' : 'sheep available'}
        </div>
      )}

      {/* ── Sheep grid ── */}
      {sheep.length > 0 && (
        <div className="sheep-grid">
          {sheep.map(s => <SheepCard key={s.id} sheep={s} />)}
        </div>
      )}

      {/* ── Skeleton loader ── */}
      {loading && (
        <div className="sheep-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="skeleton"
              style={{
                background: 'var(--surface)',
                borderRadius: 'var(--r-xl)',
                overflow: 'hidden',
                border: '1px solid var(--border)',
              }}
            >
              <div
                className="shimmer-bg"
                style={{ paddingTop: '82%' }}
              />
              <div style={{ padding: 12 }}>
                <div className="shimmer-bg skeleton" style={{ height: 13, marginBottom: 8, borderRadius: 'var(--r-sm)' }} />
                <div className="shimmer-bg skeleton" style={{ height: 13, width: '70%', marginBottom: 12, borderRadius: 'var(--r-sm)' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <div className="shimmer-bg skeleton" style={{ height: 26, width: '45%', borderRadius: 'var(--r-full)' }} />
                  <div className="shimmer-bg skeleton" style={{ height: 26, width: '45%', borderRadius: 'var(--r-full)' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && sheep.length === 0 && (
        <div className="empty-state">
          <SheepIcon size={64} color="#7A1010" opacity={0.18} />
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-2)', marginTop: 4 }}>
            {t.noSheepFound}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-3)', maxWidth: 260, lineHeight: 1.5 }}>
            {t.noSheepDesc}
          </div>
          {activeFilters > 0 && (
            <button
              className="btn btn-primary"
              onClick={clearAll}
              style={{ marginTop: 8 }}
            >
              <X size={14} />
              {lang === 'ar' ? 'مسح الفلاتر' : 'Clear Filters'}
            </button>
          )}
        </div>
      )}

      {/* ── Load more ── */}
      {hasMore && !loading && (
        <div style={{ textAlign: 'center', padding: '16px 0 28px' }}>
          <button
            className="btn btn-primary"
            onClick={() => fetchSheep(false)}
            style={{ padding: '11px 36px' }}
          >
            {t.loadMore}
          </button>
        </div>
      )}

    </main>
  );
}
